var DEFAULT = 'default';
var SHORT = 'short';
var LONG = 'long';
var FULL = 'full';
var NUMERIC = 'numeric';
var TIME_TOKENS = new Set(['Z', '+', '-']);
var HOUR = '([0-1]\\d|[2][0-3])';
var MINUTE = '([0-5]\\d)';
var SECOND = MINUTE;
var DATE = '(\\d{4})-([0]\\d|[1][0-2])-([0-2]\\d|[3][0-1])';
var TIME = "".concat(HOUR, ":").concat(MINUTE, "(:").concat(SECOND, ")?(\\.(\\d+))?([+-]").concat(HOUR, ":").concat(MINUTE, "|[Z])?");
var ISO_DATE = new RegExp("^".concat(DATE, "[Z]?$"));
var ISO_DATETIME = new RegExp("^".concat(DATE, "T").concat(TIME, "$"));
export var VALID_DATE_FORMATS = {
  DEFAULT: DEFAULT,
  SHORT: SHORT,
  LONG: LONG,
  FULL: FULL,
  undefined: undefined
};
export var VALID_FORMATS = new Set(Object.values(VALID_DATE_FORMATS));

var isNumber = function isNumber(date) {
  return typeof date === 'number';
};
/**
 * Checks if `value` is a valid `Date` with following conditions:
 *  - `value` is a `Date` type.
 *  - The string form of `value` does not display `"Invalid Date"`.
 *  - The numeric equivalent of `value` is not `NaN`.
 * Or if `value` is a number with the following conditions:
 *  - is not `NaN`
 * @param value - anything, literally.
 * @returns `true` if `value` is a valid `Date` object.
 */


var isValidDate = function isValidDate(value) {
  if (value instanceof Date) {
    return value.toString() !== 'Invalid Date' && !isNaN(value.valueOf());
  } else if (isNumber(value)) {
    return !isNaN(value);
  }

  return false;
};
/**
 * Attempt to convert given `locale` string to a valid BCP 47 language tag form.
 * Rely on browser language if the language is the same, but region is more specific.
 * @param locale Locale code to convert.
 */


var getValidLocale = function getValidLocale(locale) {
  var matchedLocale = locale && navigator.language.includes(locale) ? navigator.language : locale || navigator.language || 'en-US';
  return matchedLocale.replace(/_/g, '-');
};

var hasTimezone = function hasTimezone(input) {
  var foundT = false;

  for (var i = input.length - 1; i >= 0; i--) {
    if (input[i] === 'T') {
      foundT = true;
      continue;
    }

    if (TIME_TOKENS.has(input[i]) && !foundT) {
      return true;
    }
  }

  return false;
};
/**
 * `displayDate` takes a `Date` object and a format specifier, and returns a string representing date that is formatting according to format for the user's locale.
 * @param date - `Date` object to display as `string`.
 * @param options - options object can contain following fields:
 *   * `format` - either "default", "short", "long", or "full".
 *   * `locale` - developer designated locale (this will take precedence over browser setting, if language differs).
 *   * `useBrowserTimeZone` - use browser's timezone if true else use UTC timezone.  Defaults to true
 * @returns a locale date string corresponding to current user's locale and format options.
 */


export var displayDate = function displayDate(date) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (!isValidDate(date)) {
    throw new TypeError("The first argument must be a valid date but was \"".concat(date, "\"."));
  }

  var format = options.format,
      locale = options.locale,
      useBrowserTimeZone = options.useBrowserTimeZone;

  if (!VALID_FORMATS.has(format)) {
    var supported = Array.from(VALID_FORMATS).filter(function (f) {
      return !!f;
    }).map(function (f) {
      return JSON.stringify(f);
    });
    supported[2] = "or ".concat(supported[2]);
    throw new TypeError("The format must be either: ".concat(supported.join(', '), " but was \"").concat(format, "\"."));
  }

  var _options = {};
  _options.day = NUMERIC;
  _options.year = NUMERIC;

  switch (format) {
    case FULL:
      _options.weekday = LONG;
      _options.month = LONG;
      break;

    case LONG:
      _options.month = LONG;
      break;

    case SHORT:
      _options.month = NUMERIC;
      break;

    case DEFAULT:
    default:
      _options.month = SHORT;
  }

  if (useBrowserTimeZone === false) {
    _options.timeZone = 'UTC';
  }

  var localeId = getValidLocale(locale);
  var finalDate = isNumber(date) ? new Date(date) : date;
  return finalDate.toLocaleDateString(localeId, _options);
};
/**
 * `displayTime` takes a `Date` object and a format specifier, and returns a string representing time that is formatting according to format for the user's locale.
 * @param date - `Date` object to display as `string`.
 * @param options - options object can contain following fields:
 *   * `format` - either "short", "long", or "full".
 *   * `locale` - developer designated locale (this will take precedence over global value).
 *   * `useBrowserTimeZone` - use browser's timezone if true else use UTC timezone.
 * @returns a locale date string corresponding to current user's locale and format options.
 */

export var displayTime = function displayTime(date) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (!isValidDate(date)) {
    throw new TypeError('The first argument must be a valid date.');
  }

  var format = options.format,
      locale = options.locale,
      useBrowserTimeZone = options.useBrowserTimeZone;

  if (!VALID_FORMATS.has(format)) {
    var supported = Array.from(VALID_FORMATS).filter(function (f) {
      return !!f;
    }).map(function (f) {
      return JSON.stringify(f);
    });
    supported[2] = "or ".concat(supported[2]);
    throw new TypeError("The format must be either: ".concat(supported.join(','), "."));
  }

  var _options = {};
  _options.second = NUMERIC;
  _options.minute = NUMERIC;
  _options.hour = NUMERIC;

  if (format === FULL || format === LONG) {
    _options.timeZoneName = SHORT;
  } else if (format === SHORT) {
    delete _options.second;
  }

  if (useBrowserTimeZone === false) {
    _options.timeZone = 'UTC';
  }

  var localeId = getValidLocale(locale);
  return date.toLocaleTimeString(localeId, _options);
};
/**
 * `parseDate` converts `input` to native JS Date object.
 * @param input - UNIX epoch in milliseconds or ISO 8601.
 * @returns `Date` equivalent of `input`.
 */

export var parseDate = function parseDate(input) {
  // temporary typeguarding until all codes are converted to TS.
  if (typeof input !== 'number' && typeof input !== 'string') {
    throw new TypeError("'input' must be either number or string. Got: ".concat(input));
  }

  var _input = input;

  if (typeof _input === 'string') {
    if (ISO_DATETIME.test(_input) || ISO_DATE.test(_input)) {
      _input = hasTimezone(_input) ? _input : "".concat(_input, "Z");
    }
  }

  var result = new Date(_input);

  if (!isValidDate(result)) {
    throw new Error("".concat(JSON.stringify(input), " is not a parsable date."));
  }

  return result;
};
//# sourceMappingURL=datetime.js.map